/**
 * Permission utility functions for Duzz Commercial
 */

/**
 * Check if the user has Administrator privileges
 * (Profile 3 / "Administrador", role 'admin', or access level 13)
 *
 * @param {Object} credencials - The user credentials from StoreContext
 * @returns {boolean}
 */
export const isAdminUser = (credencials) => {
  if (!credencials) return false;

  const profileId = Number(credencials?.profile?.id ?? credencials?.profile);
  const profileName = credencials?.profile?.name?.toLowerCase() || '';
  const role = credencials?.role?.toLowerCase() || '';
  const access = Array.isArray(credencials?.profile?.access)
    ? credencials.profile.access.map(Number)
    : [];

  return (
    profileId === 3 ||
    profileName === 'administrador' ||
    profileName === 'admin' ||
    role === 'admin' ||
    access.includes(13)
  );
};

/**
 * Check if the user is a Manager or higher (Gerente ou Administrador)
 *
 * @param {Object} credencials - The user credentials from StoreContext
 * @returns {boolean}
 */
export const isManagerOrAbove = (credencials) => {
  if (!credencials) return false;
  if (isAdminUser(credencials)) return true;

  const profileId = Number(credencials?.profile?.id ?? credencials?.profile);
  const profileName = credencials?.profile?.name?.toLowerCase() || '';
  const role = credencials?.role?.toLowerCase() || '';
  const access = Array.isArray(credencials?.profile?.access)
    ? credencials.profile.access.map(Number)
    : [];

  return (
    profileId === 2 ||
    profileName === 'gerente' ||
    role === 'manager' ||
    role === 'gerente' ||
    access.some((acc) => acc >= 5)
  );
};

/**
 * Check if the user has a specific access code in their profile.access list
 * (Admins always return true)
 *
 * @param {Object} credencials - The user credentials from StoreContext
 * @param {number|string} code - The access module code
 * @returns {boolean}
 */
export const hasAccessCode = (credencials, code) => {
  if (!credencials) return false;
  if (isAdminUser(credencials)) return true;

  const access = Array.isArray(credencials?.profile?.access)
    ? credencials.profile.access.map(Number)
    : [];

  return access.includes(Number(code));
};

/**
 * Check if the user has Manager access level or higher (oriented by access level >= 10)
 *
 * @param {Object} credencials - The user credentials from StoreContext
 * @returns {boolean}
 */
export const hasManagerAccessLevel = (credencials) => {
  if (!credencials) return false;
  if (isAdminUser(credencials) || isManagerOrAbove(credencials)) return true;

  let access = [];
  if (Array.isArray(credencials?.profile?.access)) {
    access = credencials.profile.access.map(Number);
  } else if (typeof credencials?.profile?.access === 'string') {
    try {
      const parsed = JSON.parse(credencials.profile.access);
      if (Array.isArray(parsed)) access = parsed.map(Number);
    } catch (e) {}
  }

  return access.some((acc) => acc >= 10);
};

export default {
  isAdminUser,
  isManagerOrAbove,
  hasAccessCode,
  hasManagerAccessLevel,
};
